import type { ReactElement } from 'react';
import { EditableText } from '../../../components/Editable';
import { contentPatchFor, resolveNodeText, selectionFieldFor } from '../content';
import { StudioBox, toneColor, type StudioNodeRendererProps } from './box';

/**
 * Metric/Percentage/BudgetCallout/ReachCallout: bound-number-shaped copy
 * arrives as {{binding}} tokens in `content.fields[contentRef]` (spec's
 * content-conventions note) and resolves through the same `EditableText` +
 * `useBindings` path every other momentum_v2 stat does — Studio never
 * recomputes a number, it only positions the resolved token's text.
 */
function MetricLikeNode(props: StudioNodeRendererProps & { placeholder: string }): ReactElement {
  const { node, ctx, placeholder } = props;
  const { section, actions, readOnly } = ctx;
  const contentRef = node.props?.contentRef;
  const value = resolveNodeText(section, node.props);
  const color = toneColor(node.props?.tone);
  return (
    <StudioBox node={node} className="tpl-studio-metric" style={color ? { color } : undefined}>
      <EditableText
        value={value}
        field={selectionFieldFor(contentRef)}
        placeholder={placeholder}
        readOnly={readOnly}
        onSave={(next) => actions.saveContent(section, contentPatchFor(section, contentRef, next))}
      />
    </StudioBox>
  );
}

export function MetricNode(props: StudioNodeRendererProps): ReactElement {
  return <MetricLikeNode {...props} placeholder="Metric" />;
}

export function PercentageNode(props: StudioNodeRendererProps): ReactElement {
  return <MetricLikeNode {...props} placeholder="0%" />;
}

export function BudgetCalloutNode(props: StudioNodeRendererProps): ReactElement {
  return <MetricLikeNode {...props} placeholder="Budget" />;
}

export function ReachCalloutNode(props: StudioNodeRendererProps): ReactElement {
  return <MetricLikeNode {...props} placeholder="Reach" />;
}

/**
 * MetricGroup / StatRail are containers only — the resolver already gave
 * every child `Metric`/`Percentage` its own absolute box (spec: "structural
 * nodes just position children"), so the group itself contributes no visual
 * of its own.
 */
export function MetricGroupNode(): null {
  return null;
}

export function StatRailNode(): null {
  return null;
}
